/**
 * Pendo install and identity helpers.
 *
 * Loaded from the app (not GTM) so the agent and its identity ship with the
 * bundle. Nothing loads when `VITE_PENDO_API_KEY` is unset, which keeps local
 * dev and the share-link view free of it.
 *
 * Visitors are identified by email, accounts by the Cision GCID
 * (`company.meta.gc_id`), falling back to the Trajaan company id. The
 * visitor id must never change once data is collected, so do not swap either
 * key without a migration plan agreed with Pendo.
 *
 * The Intercom integration matches Pendo visitors to Intercom users through
 * Pendo's ID sync, which reads the Intercom user id off the page — so Intercom
 * must keep being identified alongside (see `services/intercom`).
 */

type PendoAgent = {
  initialize: (options: Record<string, unknown>) => void;
  identify: (options: Record<string, unknown>) => void;
  clearSession?: () => void;
  _q?: unknown[][];
};

type PendoUser = {
  email?: string;
  first_name?: string;
  last_name?: string;
  creation_date?: string;
  is_trajaan_admin?: boolean;
};

type PendoCompany = {
  id: number | string;
  company_name?: string;
  creation_date?: string;
  meta?: {gc_id?: string | null} | null;
};

const API_KEY = import.meta.env.VITE_PENDO_API_KEY as string | undefined;

let initialized = false;
let ownsAgent = false;

function getPendo(): PendoAgent | undefined {
  return (window as {pendo?: PendoAgent}).pendo;
}

/** The agent we loaded; never one another script put on the page. */
function getOwnPendo(): PendoAgent | undefined {
  return ownsAgent ? getPendo() : undefined;
}

/**
 * Pendo's install snippet: queues calls on a stub until the agent script has
 * loaded and replays them. `clearSession` is queued too, so a sign out before
 * the agent arrives is not lost behind the queued `initialize`.
 */
function loadAgent(apiKey: string): PendoAgent {
  const win = window as {pendo?: PendoAgent};
  const pendo = (win.pendo = win.pendo || ({} as PendoAgent));
  const queue = (pendo._q = pendo._q || []);
  const methods = [
    'initialize',
    'identify',
    'updateOptions',
    'pageLoad',
    'track',
    'trackAgent',
    'clearSession',
  ];
  for (const method of methods) {
    const record = pendo as unknown as Record<string, unknown>;
    record[method] =
      record[method] ||
      ((...args: unknown[]) => {
        const call = [method, ...args];
        if (method === 'initialize') {
          queue.unshift(call);
        } else {
          queue.push(call);
        }
      });
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
  document.head.appendChild(script);

  return pendo;
}

/**
 * The API sends timezone-naive UTC timestamps; parsing those as-is would read
 * them in the browser's timezone and shift them.
 */
function toIsoDate(value?: string): string | undefined {
  if (!value) {
    return undefined;
  }
  const isNaiveDateTime =
    value.includes('T') && !/(Z|[+-]\d{2}:?\d{2})$/.test(value);
  const date = new Date(isNaiveDateTime ? `${value}Z` : value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

export function buildPendoOptions(
  user: PendoUser,
  company: PendoCompany | null | undefined,
  language?: string,
) {
  const fullname = [user.first_name, user.last_name].filter(Boolean).join(' ');

  return {
    visitor: {
      id: user.email,
      email: user.email,
      firstname: user.first_name,
      lastname: user.last_name,
      fullname: fullname || undefined,
      createddate: toIsoDate(user.creation_date),
      ui_language: language,
      role: user.is_trajaan_admin ? 'admin' : 'user',
    },
    ...(company?.id
      ? {
          account: {
            id: company.meta?.gc_id || String(company.id),
            trajaan_company_id: String(company.id),
            name: company.company_name,
            creationdate: toIsoDate(company.creation_date),
          },
        }
      : {}),
  };
}

export function identifyPendoVisitor(
  user: PendoUser | null | undefined,
  company?: PendoCompany | null,
  language?: string,
): void {
  if (!API_KEY || !user?.email) {
    return;
  }

  const options = buildPendoOptions(user, company, language);

  if (!initialized) {
    initialized = true;

    // Only this module loads our agent, so any `window.pendo` already there
    // belongs to someone else (the legacy GTM "Pendo" tag, on another
    // subscription). Reusing it would send our visitors there.
    if (getPendo()) {
      console.warn('Pendo: another Pendo agent is already loaded, skipping.');
      return;
    }

    ownsAgent = true;
    loadAgent(API_KEY).initialize(options);
    return;
  }

  getOwnPendo()?.identify(options);
}

/** Drops the visitor so the next person on this browser is not tracked as them. */
export function clearPendoSession(): void {
  getOwnPendo()?.clearSession?.();
}
